import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Heart, ShoppingBag } from 'lucide-react';
import { useWishlist } from '../context/WishlistContext';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';

const ProductCard = ({ product, index = 0 }) => {
  const { isInWishlist, toggleWishlist } = useWishlist();
  const { addToCart } = useCart();
  const variant = product.variants?.[0];
  const inStock = variant?.stock > 0;
  const hasDiscount = product.compare_at_price && product.compare_at_price > product.base_price;
  const discountPercent = hasDiscount 
    ? Math.round((1 - product.base_price / product.compare_at_price) * 100)
    : 0;

  const handleWishlistClick = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const success = await toggleWishlist(product.id);
    if (success) {
      toast.success(isInWishlist(product.id) ? 'Removed from wishlist' : 'Added to wishlist!');
    }
  };

  const handleQuickAdd = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!inStock) return;
    const success = await addToCart(product, 0, 1);
    if (success) {
      toast.success('Added to cart!');
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: index * 0.05 }}
      className="group"
      data-testid={`product-card-${product.id}`}
    >
      <Link to={`/product/${product.id}`} className="block">
        {/* Image Container */}
        <div className="relative aspect-[3/4] overflow-hidden bg-[#F5F5F5]">
          <img
            src={product.images?.[0] || 'https://via.placeholder.com/400'}
            alt={product.name}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            data-testid={`product-image-${product.id}`}
          />
          
          {/* Overlay on hover */}
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-all duration-300" />
          
          {/* Badges - Top Left */}
          <div className="absolute top-3 left-3 flex flex-col gap-2">
            {product.is_new_arrival && (
              <span className="bg-[#1A1A1A] text-white text-[10px] px-3 py-1 uppercase tracking-widest font-bold" data-testid="badge-new">
                New
              </span>
            )}
            {hasDiscount && (
              <span className="bg-[#8B1F1F] text-white text-[10px] px-3 py-1 uppercase tracking-widest font-bold" data-testid="badge-sale">
                -{discountPercent}%
              </span>
            )}
            {product.is_nepora_original && (
              <span className="bg-[#D4AF37] text-white text-[10px] px-3 py-1 uppercase tracking-widest font-bold" data-testid="badge-nepora-original">
                Original
              </span>
            )}
          </div>

          {/* Quick Actions - Top Right */}
          <div className="absolute top-3 right-3 flex flex-col gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            <button 
              className={`w-9 h-9 bg-white flex items-center justify-center transition-colors ${
                isInWishlist(product.id) 
                  ? 'text-red-500 hover:bg-red-50' 
                  : 'hover:bg-[#D4AF37] hover:text-white'
              }`}
              onClick={handleWishlistClick}
              aria-label={isInWishlist(product.id) ? 'Remove from wishlist' : 'Add to wishlist'}
              data-testid={`wishlist-btn-${product.id}`}
            >
              <Heart className="w-4 h-4" fill={isInWishlist(product.id) ? 'currentColor' : 'none'} />
            </button>
          </div>

          {/* Pickup Badge - Bottom Right */}
          {product.pickup_today && (
            <div className="absolute bottom-3 right-3">
              <span className="bg-green-600 text-white text-[9px] px-2 py-1 uppercase tracking-wider font-bold">
                Pickup Today
              </span>
            </div>
          )}

          {/* Out of Stock Overlay */}
          {!inStock && (
            <div className="absolute inset-0 bg-white/80 flex items-center justify-center">
              <span className="text-sm font-bold uppercase tracking-widest text-gray-600" data-testid="out-of-stock-label">
                Sold Out
              </span>
            </div>
          )}

          {/* Quick Add - Bottom */}
          <div className="absolute bottom-0 left-0 right-0 translate-y-full group-hover:translate-y-0 transition-transform duration-300">
            <button 
              className={`w-full bg-[#1A1A1A] text-white py-3 text-xs font-bold uppercase tracking-widest transition-colors flex items-center justify-center gap-2 ${
                inStock ? 'hover:bg-[#D4AF37]' : 'opacity-50 cursor-not-allowed'
              }`}
              onClick={handleQuickAdd}
              disabled={!inStock}
              data-testid={`quick-add-${product.id}`}
            >
              <ShoppingBag className="w-4 h-4" />
              {inStock ? 'Quick Add' : 'Sold Out'}
            </button>
          </div>
        </div>

        {/* Product Info */}
        <div className="pt-4 pb-2">
          {/* Category */}
          {product.category && (
            <p className="text-[10px] text-gray-500 uppercase tracking-widest mb-1">
              {product.category}
            </p>
          )}
          
          {/* Name */}
          <h3 
            className="text-sm font-medium text-gray-900 mb-2 line-clamp-2 group-hover:text-[#8B1F1F] transition-colors"
            style={{ fontFamily: 'Lato, sans-serif' }}
            data-testid={`product-name-${product.id}`}
          >
            {product.name}
          </h3>
          
          {/* Price */}
          <div className="flex items-center gap-2">
            <span 
              className={`text-base font-bold ${hasDiscount ? 'text-[#8B1F1F]' : 'text-gray-900'}`}
              data-testid={`product-price-${product.id}`}
            >
              ${product.base_price.toFixed(2)}
            </span>
            {hasDiscount && (
              <span 
                className="text-sm text-gray-400 line-through"
                data-testid={`product-compare-price-${product.id}`}
              >
                ${product.compare_at_price.toFixed(2)}
              </span>
            )}
          </div>

          {/* Deal Tag */}
          {product.tags?.includes('$1-$10') && (
            <p className="mt-2 text-[10px] text-[#D4AF37] uppercase tracking-widest font-bold" data-testid="tag-deal">
              $10 Deal
            </p>
          )}
        </div>
      </Link>
    </motion.div>
  );
};

export default ProductCard;
